import React, { useEffect, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import styled from 'styled-components';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import GlobalStyles from './styles/GlobalStyles';
import { useTheme } from './context/ThemeContext';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Contact from './components/Contact';
import Footer from './components/Footer';
import TeamSection from './components/TeamSection';
import InTheirWords from './components/InTheirWords';
import Stats from './components/Stats';
import CemSertkaya from './components/CemSertkaya';
import DenizErenGencay from './components/DenizErenGencay';
import ProjectDetail from './components/ProjectDetail';
import CMSApp from './cms/CMSApp';
import SmoothScroll from './components/SmoothScroll';
import ChatBubble from './components/ChatBubble';
import useFooterOffset from './hooks/useFooterVisible';
import { initAnalytics, trackPageView, trackTimeOnPage, trackScrollDepth } from './utils/analytics';
import app from './firebase';
import './App.css';

const ScrollProgress = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #FF5B3A;
  transform-origin: 0%;
  z-index: 9999;
`;

const ScrollProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return <ScrollProgress style={{ scaleX }} />;
};

initAnalytics(app);

function PageTracker() {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());
  const lastScrollDepth = useRef(0);

  useEffect(() => {
    const pageName = location.pathname === '/' ? 'Home' : location.pathname.slice(1);
    trackPageView(pageName, {
      referrer: document.referrer,
      search: location.search,
    });

    pageStartTime.current = Date.now();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollDepth >= 25 && lastScrollDepth.current < 25) {
        trackScrollDepth(25);
        lastScrollDepth.current = 25;
      } else if (scrollDepth >= 50 && lastScrollDepth.current < 50) {
        trackScrollDepth(50);
        lastScrollDepth.current = 50;
      } else if (scrollDepth >= 75 && lastScrollDepth.current < 75) {
        trackScrollDepth(75);
        lastScrollDepth.current = 75;
      } else if (scrollDepth >= 90 && lastScrollDepth.current < 90) {
        trackScrollDepth(100);
        lastScrollDepth.current = 100;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      const timeOnPage = Math.round((Date.now() - pageStartTime.current) / 1000);
      trackTimeOnPage(pageName, timeOnPage);
      lastScrollDepth.current = 0;
    };
  }, [location]);

  return null;
}

function HomePage() {
  return (
    <>
      <ScrollProgressBar />
      <Header />
      <main>
        <Hero />
        <Stats />
        <About />
        <Services />
        <InTheirWords />
        <TeamSection />
        <Contact />
      </main>
      <Footer />
      <ChatBubble />
    </>
  );
}

const DevThemeToggle = styled.button`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9999;
  padding: 8px 14px;
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11px;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};
  &:hover { opacity: 0.75; }
`;

function FloatingToggle({ mode, toggleTheme }) {
  const footerOffset = useFooterOffset();
  const { pathname } = useLocation();
  if (pathname.startsWith('/cms')) return null;
  return (
    <DevThemeToggle
      onClick={toggleTheme}
      style={{ bottom: `${24 + footerOffset}px` }}
    >
      {mode === 'dark' ? '☀ light' : '☾ dark'}
    </DevThemeToggle>
  );
}

function App() {
  const { themeObject, mode, toggleTheme } = useTheme();
  return (
    <Router>
      <ThemeProvider theme={themeObject}>
        <GlobalStyles />
        <PageTracker />
        <FloatingToggle mode={mode} toggleTheme={toggleTheme} />
        <SmoothScroll>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/cemsertkaya" element={<CemSertkaya />} />
            <Route path="/denizerengencay" element={<DenizErenGencay />} />
            <Route path="/project/:projectId" element={<ProjectDetail />} />
            <Route path="/cms" element={<CMSApp />} />
          </Routes>
        </SmoothScroll>
      </ThemeProvider>
    </Router>
  );
}

export default App;
